import { Button, Empty, Flex, Typography } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlinePlus } from "react-icons/ai";

import { useCustomNotification } from "design-system/hooks";
import { useCredentials } from "sandbox/hooks/useCredentials";
import {
  removeCredentialSecret,
  storeCredentialSecret,
} from "sandbox/utils/credentialStore";
import { copyToClipboard } from "shell/api/utils";

import { CredentialCard } from "./CredentialCard";
import s from "./Credentials.module.css";
import { NewCredentialModal } from "./NewCredentialModal";
import { RevealSecretModal } from "./RevealSecretModal";

interface RevealedCredential {
  clientId: string;
  clientSecret: string;
  name: string;
}

export const Credentials = () => {
  const { t } = useTranslation(["sandbox", "common"]);
  const { credentials, create, remove, rotate } = useCredentials();
  const [modalOpen, setModalOpen] = useState(false);
  const [revealed, setRevealed] = useState<RevealedCredential | null>(null);
  const [revealMode, setRevealMode] = useState<"create" | "rotate">("create");
  const { showNotification } = useCustomNotification();

  const handleReveal = ({
    clientId,
    clientSecret,
    name,
  }: RevealedCredential) => {
    storeCredentialSecret(clientId, clientSecret);
    setRevealed({ clientId, clientSecret, name });
  };

  return (
    <section className={s.root}>
      <Flex
        align="center"
        justify="space-between"
        gap="large"
        className={s.header}
      >
        <Typography.Title level={2} className={s.title}>
          {t("labels.credentials")}
        </Typography.Title>
        <Button
          type="primary"
          size="large"
          icon={<AiOutlinePlus />}
          onClick={() => setModalOpen(true)}
        >
          {t("components.newCredentialModal.title")}
        </Button>
      </Flex>

      {credentials.length === 0 ? (
        <Empty
          image={
            <span className={s.emptyImage}>{Empty.PRESENTED_IMAGE_SIMPLE}</span>
          }
          description={
            <span className={s.emptyDescription}>
              {t("components.credentials.empty")}
            </span>
          }
          className={s.emptyState}
        />
      ) : (
        <div className={s.grid}>
          {credentials.map(credential => (
            <CredentialCard
              key={credential.id}
              credential={credential}
              onDelete={async id => {
                try {
                  await remove(id);
                  removeCredentialSecret(id);
                  showNotification({
                    type: "success",
                    message: t("common:status.success"),
                    description: t(
                      "components.credentials.message.credentialDeleted",
                      { name: credential.name },
                    ),
                  });
                } catch {
                  showNotification({
                    type: "error",
                    message: t("common:status.error"),
                    description: t(
                      "components.credentials.message.credentialDeleteFailed",
                      { name: credential.name },
                    ),
                  });
                }
              }}
              onRotate={async id => {
                const result = await rotate(id);
                if (result) {
                  setRevealMode("rotate");
                  handleReveal({
                    ...credential,
                    clientSecret: result.clientSecret,
                  });
                }
              }}
            />
          ))}
        </div>
      )}

      {modalOpen && (
        <NewCredentialModal
          open={modalOpen}
          onCancel={() => setModalOpen(false)}
          onSubmit={async name => {
            setModalOpen(false);
            const credential = await create(name);
            setRevealMode("create");
            handleReveal(credential);
          }}
        />
      )}

      {revealed && (
        <RevealSecretModal
          open={revealed !== null}
          credential={revealed}
          isRotation={revealMode === "rotate"}
          onClose={() => {
            copyToClipboard(revealed.clientSecret);
            setRevealed(null);
            if (revealMode === "rotate") {
              showNotification({
                type: "success",
                message: t("common:status.success"),
                description: t("components.credentials.message.secretRotated"),
              });
            }
          }}
        />
      )}
    </section>
  );
};
